import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import {
	editSubcategoryRestaurantPropertiesInitialState,
	EditSubcategoryRestaurantPropertiesContext,
	TTypeOfModalWindow,
} from './editSubcategoryRestaurantPropertiesContext';
import { IAppContext, AppContext } from 'appState/appContext';
import { editSubcategoryRestaurantPropertiesReducer } from './editSubcategoryRestaurantPropertiesReducer';
import { EditSubcategoryRestaurantPropertiesActionTypes } from './editSubcategoryRestaurantPropertiesActions';
import {
	UserRoles,
	IGetSubcategoryInfoV2,
	IUpdateSubcategoryRestaurantProperties,
	IGetSubcategoryRestaurantProperties,
	IGetSubcategoryRestaurantPropertiesArguments,
} from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { getSubcategoryInfoV2, getSubcategoryRestaurantProperties } from 'backend/subcategories';
import { useFetchData } from 'hooks/useFetchData';

export const EditSubcategoryRestaurantPropertiesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(
		editSubcategoryRestaurantPropertiesReducer,
		editSubcategoryRestaurantPropertiesInitialState,
	);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const subcategoryID = params.get('subcategoryID');

	const { response: subcategoryInfoResponse, loading: subcategoryInfoLoading } = useFetchData<
		IGetSubcategoryInfoV2,
		string
	>(subcategoryID ? getSubcategoryInfoV2 : null, subcategoryID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetSubcategoryRestaurantProperties, IGetSubcategoryRestaurantPropertiesArguments>(
		state.IDSelectedRestaurant ? getSubcategoryRestaurantProperties : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			subcategoryId: state.subcategoryId,
		},
	);

	useEffect(() => {
		subcategoryInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [subcategoryInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (subcategoryInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const fetchedRestaurants = hasAccess
				? subcategoryInfoResponse.data.restaurants
				: subcategoryInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: EditSubcategoryRestaurantPropertiesActionTypes.SET_ALL_DATA,
				payload: {
					subcategoryId: subcategoryInfoResponse.data.id,
					subcategoryName: subcategoryInfoResponse.data.name || '',
					customerName: subcategoryInfoResponse.data.customer.name || '',
					categoryName: subcategoryInfoResponse.data.category.name || '',
					hasSchedulePriority: subcategoryInfoResponse.data.overrideScheduleAvailability,
					activationSchedule: subcategoryInfoResponse.data.activationCron || '',
					deactivationSchedule: subcategoryInfoResponse.data.deactivationCron || '',
					fetchedRestaurants,
				},
			});
		}
	}, [subcategoryID, loggedUser.restaurants, hasAccess, subcategoryInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: EditSubcategoryRestaurantPropertiesActionTypes.SET_DATA_FROM_RESTAURANT,
				payload: {
					dishes: restaurantPropertiesResponse.data.dishes.map((dish) => ({
						id: dish.id,
						name: dish.name || '',
					})),
					activationSchedule: restaurantPropertiesResponse.data.activationCron || '',
					deactivationSchedule: restaurantPropertiesResponse.data.deactivationCron || '',
					availableForRepetition: restaurantPropertiesResponse.data.availableForRepetition,
					isHidden: restaurantPropertiesResponse.data.isHidden,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: EditSubcategoryRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.IDSelectedRestaurant]);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: EditSubcategoryRestaurantPropertiesActionTypes.SET_ID_RESTAURANT,
				payload: {
					IDSelectedRestaurant: value,
					dishes: value ? state.dishes : [],
					activationSchedule: value ? state.activationSchedule : '',
					deactivationSchedule: value ? state.deactivationSchedule : '',
					availableForRepetition: value ? state.availableForRepetition : false,
					isHidden: value ? state.isHidden : false,
				},
			});
		},
		[
			state.dishes,
			state.activationSchedule,
			state.deactivationSchedule,
			state.availableForRepetition,
			state.isHidden,
		],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const toggleAvailableForRepetition = useCallback(() => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.TOGGLE_AVAILABLE_FOR_REPETITION,
		});
	}, []);

	const toggleIsHidden = useCallback(() => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.TOGGLE_IS_HIDDEN,
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateSubcategoryRestaurantProperties>) => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditSubcategoryRestaurantPropertiesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditSubcategoryRestaurantPropertiesContext.Provider
			value={{
				refetchRestPropsData,
				setIDRestaurant,
				changeActivationSchedule,
				changeDeactivationSchedule,
				toggleAvailableForRepetition,
				toggleIsHidden,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				customerName: state.customerName,
				categoryName: state.categoryName,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				subcategoryId: state.subcategoryId,
				subcategoryName: state.subcategoryName,
				dishes: state.dishes,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				availableForRepetition: state.availableForRepetition,
				isHidden: state.isHidden,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditSubcategoryRestaurantPropertiesContext.Provider>
	);
};
